import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import { type LoginResponse } from '../api/auth'
import { fetchAllProducts, fetchDepartments, type Department, type Product } from '../api/catalog'
import {
  crossSellApi,
  type CrossSellRule,
  type CrossSellRuleInput,
  type ProductPenetration,
} from '../api/crossSell'
import type { ActionPriority } from '../api/contactOverview'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  FormGrid,
  PaneLayout, Button, Callout, Card, CardContent, CardHeader, Checkbox, DataTable, DataTableEmptyRow, IconButton,
  LinearProgress, LoadingState, Page, PageHeader, Select, StatusPill, Tabs, TextField, useConfirm, List,
  ListItem,
} from '../components/ui'

/** F-100: avviso dopo una clonazione. */
const CLONE_NOTICE = 'Copia creata: è inattiva (in bozza). Controllala e attivala quando è pronta.'

/**
 * F-076 (TASK-0330, REQ-247-001 / REQ-246-001) — penetrazione di settori e prodotti sul portafoglio clienti
 * (ruoli Manager) e, per l'Admin, la gestione delle regole di cross-selling. Nessun importo e nessuna classifica
 * per agente: si contano clienti, non ricavi. Le percentuali arrivano dal server come frazioni 0..1.
 */

type Tab = 'penetration' | 'rules'

const PRIORITIES: ActionPriority[] = ['Alta', 'Media', 'Bassa']

function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

const formatNum = (v: number): string => v.toLocaleString('it-IT')

/** Frazione 0..1; null = portafoglio vuoto (mai "0%"). */
function formatPct(v: number | null): string {
  if (v === null) return '—'
  return (v * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
}

function ShareBar({ share }: { share: number | null }) {
  const width = share === null ? 0 : Math.max(share * 100, share > 0 ? 2 : 0)
  return (
    <LinearProgress value={width} aria-label="Penetrazione" />
  )
}

const EMPTY_FORM: CrossSellRuleInput = {
  name: '',
  ifHasDepartmentId: null,
  ifHasProductId: null,
  ifLacksDepartmentId: null,
  ifLacksProductId: null,
  customerSegment: '',
  suggestionLabel: '',
  priority: 'Media',
  isActive: true,
}

function toNum(v: string): number | null {
  return v === '' ? null : Number(v)
}

export function ProductPenetrationPage({ auth }: { auth: LoginResponse }) {
  const confirm = useConfirm()
  const admin = isAdmin(auth)
  const [tab, setTab] = useState<Tab>('penetration')
  const [report, setReport] = useState<ProductPenetration | null>(null)
  const [rules, setRules] = useState<CrossSellRule[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState<CrossSellRuleInput>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const loadReport = useCallback(() => {
    setLoading(true)
    setError('')
    crossSellApi.penetration()
      .then(setReport)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento del report'))
      .finally(() => setLoading(false))
  }, [])

  const loadRules = useCallback(() => {
    crossSellApi.list()
      .then(setRules)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento delle regole'))
  }, [])

  useEffect(() => { loadReport() }, [loadReport])

  useEffect(() => {
    if (!admin) return
    loadRules()
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
    fetchAllProducts().then(r => setProducts(r.items)).catch(() => setProducts([]))
  }, [admin, loadRules])

  const deptName = (id: number | null) => (id === null ? null : departments.find(d => d.id === id)?.name ?? `Settore #${id}`)
  const productName = (id: number | null) => (id === null ? null : products.find(p => p.id === id)?.name ?? `Prodotto #${id}`)

  function startEdit(rule: CrossSellRule | null) {
    setError('')
    if (rule === null) {
      setForm(EMPTY_FORM)
      setEditingId('new')
      return
    }
    setForm({
      name: rule.name,
      ifHasDepartmentId: rule.ifHasDepartmentId,
      ifHasProductId: rule.ifHasProductId,
      ifLacksDepartmentId: rule.ifLacksDepartmentId,
      ifLacksProductId: rule.ifLacksProductId,
      customerSegment: rule.customerSegment ?? '',
      suggestionLabel: rule.suggestionLabel,
      priority: rule.priority,
      isActive: rule.isActive,
    })
    setEditingId(rule.id)
  }

  // F-100: dopo "Clona" la copia (inattiva) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(rules, r => startEdit(r))

  async function save() {
    setSaving(true)
    setError('')
    const body: CrossSellRuleInput = { ...form, customerSegment: form.customerSegment?.trim() || null }
    try {
      if (editingId === 'new') await crossSellApi.create(body)
      else if (editingId !== null) await crossSellApi.update(editingId, body)
      setEditingId(null)
      loadRules()
      loadReport()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito')
    } finally {
      setSaving(false)
    }
  }

  async function remove(rule: CrossSellRule) {
    if (!(await confirm({ title: 'Eliminare la regola?', message: `Eliminare la regola "${rule.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    setError('')
    try {
      await crossSellApi.remove(rule.id)
      loadRules()
      loadReport()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eliminazione non riuscita')
    }
  }

  function describe(rule: CrossSellRule): string {
    const has = [deptName(rule.ifHasDepartmentId), productName(rule.ifHasProductId)].filter(Boolean)
    const lacks = [deptName(rule.ifLacksDepartmentId), productName(rule.ifLacksProductId)].filter(Boolean)
    const parts: string[] = []
    if (has.length > 0) parts.push(`ha ${has.join(' e ')}`)
    parts.push(`non ha ${lacks.join(' né ')}`)
    if (rule.customerSegment) parts.push(`segmento ${rule.customerSegment}`)
    return parts.join(', ')
  }

  return (
    <Page>
      <PageHeader title="Penetrazione prodotti" icon="donut_small" actions={<ReportExportButtons title="Penetrazione prodotti" />} />

      {admin && (
        <Tabs
          aria-label="Sezioni"
          value={tab}
          onChange={id => setTab(id as Tab)}
          items={[
            { id: 'penetration', label: 'Penetrazione' },
            { id: 'rules', label: 'Regole di cross-selling' },
          ]}
        />
      )}

      {error && <Callout tone="error">{error}</Callout>}

      {tab === 'penetration' && (
        <>
          {loading && !report && <LoadingState />}
          {report && (
            <>
              <div className="text-muted md-typescale-body-small">
                Perimetro: {report.perimeter}. Clienti nel portafoglio: <strong>{formatNum(report.totalClients)}</strong>.
              </div>

              <PaneLayout
                variant="supporting"
                main={<>
                  <Card padding="none">
                    <CardHeader title="Per settore" />
                    <DataTable variant="plain">
                      <thead>
                        <tr><th>Settore</th><th className="text-end">Clienti</th><th className="text-end">Penetrazione</th><th className="md-u-w-30pct" /></tr>
                      </thead>
                      <tbody>
                        {report.departments.length === 0 && <DataTableEmptyRow colSpan={4} title="Nessun settore." />}
                        {report.departments.map(d => (
                          <tr key={d.departmentId}>
                            <td>{d.name}</td>
                            <td className="text-end md-num">{formatNum(d.clients)}</td>
                            <td className="text-end md-num">{formatPct(d.share)}</td>
                            <td><ShareBar share={d.share} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </Card>

                  {report.products.length > 0 && (
                    <Card padding="none">
                      <CardHeader title="Per prodotto" />
                      <DataTable variant="plain">
                        <thead>
                          <tr><th>Prodotto</th><th className="text-end">Clienti</th><th className="text-end">Penetrazione</th></tr>
                        </thead>
                        <tbody>
                          {report.products.map(p => (
                            <tr key={p.productId}>
                              <td>{p.name}</td>
                              <td className="text-end md-num">{formatNum(p.clients)}</td>
                              <td className="text-end md-num">{formatPct(p.share)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </Card>
                  )}
                </>}
                pane={<>
                  <Card padding="none">
                    <CardHeader title="Clienti per numero di settori" />
                    <List aria-label="Clienti per numero di settori">
                      {report.bySectorCount.map(b => (
                        <ListItem
                          key={b.label}
                          headline={b.label}
                          trailing={<span className="md-num">{formatNum(b.clients)} <span className="text-muted md-typescale-body-small">({formatPct(b.share)})</span></span>}
                        />
                      ))}
                    </List>
                  </Card>

                  <Card>
                    <CardHeader title="Suggerimenti aperti" />
                    <CardContent>
                      <div className="d-flex gap-3 mb-2">
                        <span><StatusPill tone="error">Alta</StatusPill> {formatNum(report.openSuggestions.byPriority.alta)}</span>
                        <span><StatusPill tone="warning">Media</StatusPill> {formatNum(report.openSuggestions.byPriority.media)}</span>
                        <span><StatusPill tone="neutral">Bassa</StatusPill> {formatNum(report.openSuggestions.byPriority.bassa)}</span>
                      </div>
                      {report.openSuggestions.byRule.length === 0 ? (
                        <div className="text-muted md-typescale-body-small">Nessun suggerimento aperto.</div>
                      ) : (
                        <ul className="md-typescale-body-small mb-0">
                          {report.openSuggestions.byRule.map(r => (
                            <li key={r.ruleId}>{r.label} — {formatNum(r.clients)} clienti ({r.priority})</li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                </>}
              />

              <ul className="md-typescale-body-small text-muted">
                {report.notes.map(n => <li key={n}>{n}</li>)}
              </ul>
            </>
          )}
        </>
      )}

      {tab === 'rules' && admin && (
        <>
          {cloneNotice && (
            <Callout
              tone="info"
              action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setCloneNotice('')} />}
            >
              {cloneNotice}
            </Callout>
          )}
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="text-muted md-typescale-body-small">
              Una regola suggerisce una proposta quando il cliente ha certi beni e NON ne ha altri. Serve almeno una condizione “non ha”.
            </div>
            <Button size="sm" variant="tonal" onClick={() => startEdit(null)}>Nuova regola</Button>
          </div>

          {editingId !== null && (
            <Card>
              <CardHeader title={editingId === 'new' ? 'Nuova regola' : 'Modifica regola'} />
              <CardContent>
                <FormGrid columns={4}>
                  <div className="md-span-2">
                    <TextField label="Nome (max 100)" maxLength={100} value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="md-span-2">
                    <TextField label="Suggerimento (max 150)" maxLength={150} value={form.suggestionLabel}
                      onChange={e => setForm(f => ({ ...f, suggestionLabel: e.target.value }))} />
                  </div>
                  <div>
                    <Select label="Se ha il settore" value={form.ifHasDepartmentId ?? ''}
                      onChange={e => setForm(f => ({ ...f, ifHasDepartmentId: toNum(e.target.value) }))}>
                      <option value="">—</option>
                      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <Select label="Se ha il prodotto" value={form.ifHasProductId ?? ''}
                      onChange={e => setForm(f => ({ ...f, ifHasProductId: toNum(e.target.value) }))}>
                      <option value="">—</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <Select label="E NON ha il settore" value={form.ifLacksDepartmentId ?? ''}
                      onChange={e => setForm(f => ({ ...f, ifLacksDepartmentId: toNum(e.target.value) }))}>
                      <option value="">—</option>
                      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <Select label="E NON ha il prodotto" value={form.ifLacksProductId ?? ''}
                      onChange={e => setForm(f => ({ ...f, ifLacksProductId: toNum(e.target.value) }))}>
                      <option value="">—</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <TextField label="Segmento (facoltativo)" maxLength={50} value={form.customerSegment ?? ''}
                      onChange={e => setForm(f => ({ ...f, customerSegment: e.target.value }))} />
                  </div>
                  <div>
                    <Select label="Priorità" value={form.priority}
                      onChange={e => setForm(f => ({ ...f, priority: e.target.value as ActionPriority }))}>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </Select>
                  </div>
                  <div className="d-flex align-items-end">
                    <Checkbox id="rule-active" label="Attiva" checked={form.isActive}
                      onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                  </div>
                </FormGrid>
                <div className="d-flex gap-2 mt-3">
                  <Button size="sm" variant="tonal" disabled={saving} onClick={save}>Salva</Button>
                  <Button size="sm" variant="text" onClick={() => setEditingId(null)}>Annulla</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card padding="none">
            <DataTable variant="plain">
              <thead>
                <tr><th>Nome</th><th>Condizione</th><th>Suggerimento</th><th>Priorità</th><th>Stato</th><th /></tr>
              </thead>
              <tbody>
                {rules.length === 0 && <DataTableEmptyRow colSpan={6} title="Nessuna regola configurata." />}
                {rules.map(r => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td className="md-typescale-body-small text-muted">Se il cliente {describe(r)}</td>
                    <td>{r.suggestionLabel}</td>
                    <td>{r.priority}</td>
                    <td>{r.isActive ? 'Attiva' : 'Disattivata'}</td>
                    <td className="text-nowrap text-end">
                      <CloneConfigurationButton
                        entityLabel="regola di cross-selling"
                        url={`/api/cross-sell-rules/${r.id}/clone`}
                        defaultName={r.name}
                        note="Si copiano condizioni, segmento, suggerimento e priorità. La copia nasce disattivata."
                        onCloned={async id => { loadRules(); setCloneNotice(CLONE_NOTICE); openAfterClone(id) }}
                      />
                      <Button size="sm" variant="outlined" className="me-1" onClick={() => startEdit(r)}>Modifica</Button>
                      <Button size="sm" variant="outlined" onClick={() => remove(r)}>Elimina</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Card>
        </>
      )}
    </Page>
  )
}
